import { useMemo, useState } from 'react';
import type { Flight, Airport } from '@/types';

/**
 * Helper function to check if an airport matches the search query
 * @param airport - The airport object to search
 * @param query - The lowercase search query
 * @returns true if any airport field matches the query
 */
function airportMatchesQuery(airport: Airport | undefined, query: string): boolean {
  if (!airport) return false;
  
  return (
    (airport.iata?.toLowerCase().includes(query) ?? false) ||
    (airport.name?.toLowerCase().includes(query) ?? false) ||
    (airport.city?.toLowerCase().includes(query) ?? false) ||
    (airport.country?.toLowerCase().includes(query) ?? false)
  );
}

/**
 * Custom hook to filter flights based on search query
 * Searches across airport codes, names, cities, countries, and flight notes
 */
export function useFlightSearch(flights: Flight[]) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFlights = useMemo(() => {
    if (!searchQuery.trim()) {
      return flights;
    }

    const query = searchQuery.toLowerCase().trim();

    return flights.filter((flight) => {
      // Search in from airport
      const fromMatches = airportMatchesQuery(flight.fromAirport, query);

      // Search in to airport
      const toMatches = airportMatchesQuery(flight.toAirport, query);

      // Search in notes/description
      const notesMatch = flight.notes?.toLowerCase().includes(query) || false;

      return fromMatches || toMatches || notesMatch;
    });
  }, [flights, searchQuery]);

  return {
    searchQuery,
    setSearchQuery,
    filteredFlights,
  };
}
